import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { DashboardService } from '../../../services/dashboard.service';
import { ActionItem } from '../../../interfaces/dashboard.interface';

/** Critical first; the server's order otherwise. */
export function criticalFirst(items: ActionItem[]): ActionItem[] {
    return [...items.filter(i => i.severity === 'CRITICAL'), ...items.filter(i => i.severity !== 'CRITICAL')];
}

export default function ActionCenter({ institutionId }: { institutionId: string }) {
    const [items, setItems] = useState<ActionItem[] | null>(null);
    const navigate = useNavigate();
    const { t } = useTranslation('dashboard');

    useEffect(() => {
        let live = true;
        DashboardService.getActionCenter(institutionId)
            .then(x => { if (live) setItems(criticalFirst(Array.isArray(x) ? x : [])); })
            .catch(() => { if (live) setItems([]); });
        return () => { live = false; };
    }, [institutionId]);

    // The server sends English text; known items are translated by id, unknown ones fall back to it.
    const text = (item: ActionItem, part: 'title' | 'detail' | 'action', fallback: string) =>
        t(`actionCenter.items.${item.id}.${part}`, { count: item.count, defaultValue: fallback });

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{t('actionCenter.title')}</Typography>
            {items === null && <Typography variant="body2" color="text.secondary">{t('actionCenter.loading')}</Typography>}
            {items !== null && items.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('actionCenter.empty')}</Typography>
            )}
            {/* Tiles sharing the row, not one full-width row per item (that left most of the line empty). */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(340px, 1fr))' } }}>
                {items?.map(item => {
                    const critical = item.severity === 'CRITICAL';
                    const tone = critical ? 'error' : 'warning';
                    return (
                        <Box
                            key={item.id}
                            data-testid={`action-${item.id}`}
                            sx={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, p: 1.5, borderRadius: 1,
                                bgcolor: 'action.hover', borderLeft: 4, borderColor: `${tone}.main`, minWidth: 0,
                            }}
                        >
                            <Box sx={{ minWidth: 44, textAlign: 'center' }}>
                                {item.count > 0
                                    ? <Typography variant="h4" fontWeight={700} color={`${tone}.main`} lineHeight={1.1}>{item.count}</Typography>
                                    : (critical ? <ErrorOutlineIcon color="error" fontSize="large" /> : <WarningAmberIcon color="warning" fontSize="large" />)}
                            </Box>
                            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>{text(item, 'title', item.title)}</Typography>
                                <Typography variant="caption" color="text.secondary" component="div">{text(item, 'detail', item.detail)}</Typography>
                                <Chip
                                    size="small" label={t(`actionCenter.severity.${item.severity}`)} color={tone} variant="outlined"
                                    sx={{ mt: 0.5, height: 20, fontSize: 10 }}
                                />
                            </Box>
                            <Button size="small" variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate(item.link)}>
                                {text(item, 'action', item.actionLabel)}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}
